import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { NumericComparator, ReactionCategory } from '@peruselab/api-client/moleculeSearch';
import { PERIODIC_TABLE } from '@peruselab/structure-editor/chemistry/periodicTable';
import type { SearchResultFilters } from '../../hooks/useSearchResultFilters';
import type { UseStructuralConstraintsResult } from '../../hooks/useStructuralConstraints';
import { Icon } from '../Icon';
import { ALL_CATEGORIES, ALL_TYPES, type SortKey } from './searchResultsModel';

// #563: independent checkboxes, in the reference screenshot's own order (Reactants / Conditions /
// Products). "Intermediates" is deliberately not offered -- it has no backing concept yet
// (ParticipantRole/ReactionBucket have no such role; ADR 0008 scopes reactions as single-step
// only), see docs/right-rail-redesign.md and #563's own scope note.
const REACTION_CATEGORIES: readonly ReactionCategory[] = ['REACTANT', 'CONDITION', 'PRODUCT'];

// #566: Cycle Sizes / Atom Symbols rows share this exact comparator set (#564/#565's backend).
const COMPARATORS: readonly NumericComparator[] = ['EQ', 'LT', 'LE', 'GT', 'GE'];

interface SearchFilterRailProps {
  readonly filters: SearchResultFilters;
  readonly reactionBuckets: ReadonlySet<ReactionCategory>;
  readonly onToggleReactionBucket: (category: ReactionCategory) => void;
  readonly structuralConstraints: UseStructuralConstraintsResult;
}

// The search-results filter rail (match type / entity type / minimum similarity / sort, plus
// the server-side reaction-role filter). Bound to `useSearchResultFilters` + `useMoleculeSearch`,
// so it can render in-panel (SearchResultsPanel, /draw) or in the FILTER tab of the workspace
// shell (/draw/next, ADR 0007 P6b).
export function SearchFilterRail({
  filters,
  reactionBuckets,
  onToggleReactionBucket,
  structuralConstraints
}: SearchFilterRailProps) {
  const { t } = useTranslation();
  return (
    <fieldset className="search-results-filters">
      <legend>{t('searchResultsPanel.filters.legend')}</legend>

      <FilterSection
        label={t('searchResultsPanel.filters.matchType')}
        checked={ALL_CATEGORIES.filter((category) => filters.categories.has(category)).length}
        total={ALL_CATEGORIES.length}
      >
        {ALL_CATEGORIES.map((category) => (
          <label key={category}>
            <input
              type="checkbox"
              checked={filters.categories.has(category)}
              onChange={() => filters.toggleCategory(category)}
            />
            {t(`searchResultsPanel.categories.${category}`)}
          </label>
        ))}
      </FilterSection>

      <FilterSection
        label={t('searchResultsPanel.filters.show')}
        checked={ALL_TYPES.filter((type) => filters.types.has(type)).length}
        total={ALL_TYPES.length}
      >
        {ALL_TYPES.map((type) => (
          <label key={type}>
            <input type="checkbox" checked={filters.types.has(type)} onChange={() => filters.toggleType(type)} />
            {t(`searchResultsPanel.${type}`)}
          </label>
        ))}
      </FilterSection>

      {filters.types.has('reactions') ? (
        <FilterSection
          label={t('searchResultsPanel.filters.reactionCategory')}
          checked={REACTION_CATEGORIES.filter((category) => reactionBuckets.has(category)).length}
          total={REACTION_CATEGORIES.length}
        >
          {REACTION_CATEGORIES.map((category) => (
            <label key={category}>
              <input
                type="checkbox"
                checked={reactionBuckets.has(category)}
                onChange={() => onToggleReactionBucket(category)}
              />
              {t(`searchResultsPanel.reactionCategories.${category}`)}
            </label>
          ))}
        </FilterSection>
      ) : null}

      <label className="search-results-filter-inline">
        <span>{t('searchResultsPanel.filters.minScore')}</span>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={filters.minScore}
          onChange={(event) => filters.setMinScore(Number(event.target.value))}
        />
        <output>{filters.minScore.toFixed(2)}</output>
      </label>

      <label className="search-results-filter-inline">
        <span>{t('searchResultsPanel.filters.sortBy')}</span>
        <select value={filters.sortKey} onChange={(event) => filters.setSortKey(event.target.value as SortKey)}>
          <option value="scoreDesc">{t('searchResultsPanel.sortOptions.scoreDesc')}</option>
          <option value="scoreAsc">{t('searchResultsPanel.sortOptions.scoreAsc')}</option>
          <option value="title">{t('searchResultsPanel.sortOptions.title')}</option>
        </select>
      </label>

      <details className="search-results-structural-filter">
        <summary>{t('searchResultsPanel.filters.cycleSizes')}</summary>
        <div className="search-results-structural-filter-body">
          {structuralConstraints.cycleSizeRows.map((row) => (
            <div key={row.id} className="search-results-structural-filter-row">
              <input
                type="number"
                min={3}
                aria-label={t('searchResultsPanel.filters.ringSize')}
                value={row.ringSize}
                onChange={(event) =>
                  structuralConstraints.updateCycleSizeRow(row.id, { ringSize: Number(event.target.value) })
                }
              />
              <select
                aria-label={t('searchResultsPanel.filters.comparator')}
                value={row.comparator}
                onChange={(event) =>
                  structuralConstraints.updateCycleSizeRow(row.id, {
                    comparator: event.target.value as NumericComparator
                  })
                }
              >
                {COMPARATORS.map((comparator) => (
                  <option key={comparator} value={comparator}>
                    {t(`searchResultsPanel.comparators.${comparator}`)}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min={0}
                aria-label={t('searchResultsPanel.filters.ringCount')}
                value={row.count}
                onChange={(event) =>
                  structuralConstraints.updateCycleSizeRow(row.id, { count: Number(event.target.value) })
                }
              />
              <button
                type="button"
                className="icon-button"
                aria-label={t('searchResultsPanel.filters.removeCycleSizeRow')}
                title={t('searchResultsPanel.filters.removeCycleSizeRow')}
                onClick={() => structuralConstraints.removeCycleSizeRow(row.id)}
              >
                <Icon name="close" size="1rem" />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="icon-button"
            aria-label={t('searchResultsPanel.filters.addCycleSizeRow')}
            title={t('searchResultsPanel.filters.addCycleSizeRow')}
            onClick={structuralConstraints.addCycleSizeRow}
          >
            <Icon name="add" size="1rem" />
          </button>
        </div>
      </details>

      <details className="search-results-structural-filter">
        <summary>{t('searchResultsPanel.filters.atomSymbols')}</summary>
        <div className="search-results-structural-filter-body">
          {structuralConstraints.atomSymbolRows.map((row) => (
            <div key={row.id} className="search-results-structural-filter-row">
              <select
                aria-label={t('searchResultsPanel.filters.element')}
                value={row.symbol}
                onChange={(event) => structuralConstraints.updateAtomSymbolRow(row.id, { symbol: event.target.value })}
              >
                {PERIODIC_TABLE.map((cell) => (
                  <option key={cell.z} value={cell.symbol}>
                    {cell.symbol} — {cell.name}
                  </option>
                ))}
              </select>
              <select
                aria-label={t('searchResultsPanel.filters.comparator')}
                value={row.comparator}
                onChange={(event) =>
                  structuralConstraints.updateAtomSymbolRow(row.id, {
                    comparator: event.target.value as NumericComparator
                  })
                }
              >
                {COMPARATORS.map((comparator) => (
                  <option key={comparator} value={comparator}>
                    {t(`searchResultsPanel.comparators.${comparator}`)}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min={0}
                aria-label={t('searchResultsPanel.filters.atomCount')}
                value={row.count}
                onChange={(event) =>
                  structuralConstraints.updateAtomSymbolRow(row.id, { count: Number(event.target.value) })
                }
              />
              <button
                type="button"
                className="icon-button"
                aria-label={t('searchResultsPanel.filters.removeAtomSymbolRow')}
                title={t('searchResultsPanel.filters.removeAtomSymbolRow')}
                onClick={() => structuralConstraints.removeAtomSymbolRow(row.id)}
              >
                <Icon name="close" size="1rem" />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="icon-button"
            aria-label={t('searchResultsPanel.filters.addAtomSymbolRow')}
            title={t('searchResultsPanel.filters.addAtomSymbolRow')}
            onClick={structuralConstraints.addAtomSymbolRow}
          >
            <Icon name="add" size="1rem" />
          </button>
        </div>
      </details>
    </fieldset>
  );
}

/**
 * #676 -- a checkbox group as a disclosure, collapsed by default so the tab's lower controls stay
 * in view. The summary says how much of the group is on ("Match type · all", "Show · 3 of 4").
 */
function FilterSection({
  label,
  checked,
  total,
  children
}: {
  readonly label: string;
  readonly checked: number;
  readonly total: number;
  readonly children: ReactNode;
}) {
  const { t } = useTranslation();
  const status =
    checked === total
      ? t('searchResultsPanel.filters.sectionAll')
      : checked === 0
        ? t('searchResultsPanel.filters.sectionNone')
        : t('searchResultsPanel.filters.sectionSome', { count: checked, total });
  return (
    <details className="search-results-filter-section">
      <summary>
        <span className="search-results-filter-label">{label}</span>
        <span className="search-results-filter-status"> · {status}</span>
      </summary>
      <div className="search-results-filter-group" role="group" aria-label={label}>
        {children}
      </div>
    </details>
  );
}
